import { useState } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { uniqueId } from 'lodash';
import { featureCollection } from '@turf/turf';
import { v4 as uuidv4 } from 'uuid';
import { Feature } from 'geojson';
import { showError } from '../../../utils/toast';
import { LandAdvisorMessage } from '../../../types/landAdvisor';

const LandAdvisorAgentQuery = gql`
  query QueryLandAdvisor($input: String!, $sessionId: String, $geojson: JSON) {
    queryLandAdvisor(input: $input, sessionId: $sessionId, geojson: $geojson) {
      output
    }
  }
`;

const useLandAdvisorAgent = ({ parcels }: { parcels: Feature[] }) => {
  const [messages, setMessages] = useState<LandAdvisorMessage[]>([]);
  const [sessionId, setSessionId] = useState(uuidv4());

  const [queryAgent, { loading }] = useLazyQuery(LandAdvisorAgentQuery);

  const resetAgent = () => {
    setMessages([]);
    setSessionId(uuidv4());
  };

  const sendMessage = (text: string) => {
    // Append message from user
    const userMessage: LandAdvisorMessage = {
      text,
      role: 'user',
      id: uniqueId(),
    };
    setMessages((prev) => [...prev, userMessage]);

    queryAgent({
      variables: {
        sessionId,
        input: text,
        geojson: featureCollection(parcels),
      },
    })
      .then(({ data }) => {
        const { output } = data.queryLandAdvisor;
        const agentMessage: LandAdvisorMessage = {
          text: output,
          role: 'agent',
          id: uniqueId(),
        };
        // Append message from agent
        setMessages((prev) => [...prev, agentMessage]);
      })
      .catch((err) => {
        const message = err.message.includes(
          'Error querying Land Advisor service',
        )
          ? 'Land Advisor is currently unavailable. Please try again later.'
          : 'An error occurred while communicating with Land Advisor. Please try again.';
        showError(message);
      });
  };

  return {
    loading,
    messages,
    resetAgent,
    sendMessage,
    setMessages,
  };
};

export default useLandAdvisorAgent;
